import { RadioButtons, TextField } from '@honeycomb-npm/honeycomb-react';

import {
    useEffect,
    useRef,
    useState,
} from 'react';

import { Icon } from '@honeycomb-npm/honeycomb-react';

import {
    GenericBladeHiddenInput,
    GenericBladeParsedDataType,
    GenericFormAEM,
    GenericRadioForm,
    NodePayload,
    ValidationInfo,
} from '../genericBladeTypes';
import { HelptextIconWrapper } from './BladeRadioStyled';

type Props = {
    id: string;
    genericFormAEM: Record<string, GenericFormAEM>;
    genericFormApiData?: GenericBladeParsedDataType;
    handleChange: (node: string, value: NodePayload | null) => void;
    validations?: ValidationInfo;
    hiddenInputs?: Record<string, GenericBladeHiddenInput>;
    errors: Record<string, string>;
    showHelptextIcon?: boolean;
};

export const BladeRadio = ({
    id,
    genericFormAEM,
    genericFormApiData,
    handleChange,
    validations,
    hiddenInputs,
    errors,
    showHelptextIcon,
}: Props): JSX.Element => {
    const [selectedRadio, setSelectedRadio] = useState('');
    const [isRadioValid, setIsRadioValid] = useState(true);
    const [errorRadio, setErrorRadio] = useState('');

    const [hiddenInputValue, setHiddenInputValue] = useState('');
    const [isHiddenInputValid, setIsHiddenInputValid] = useState(true);
    const [errorHiddenInput, setErrorHiddenInput] = useState('');
    const [showHelptext, setShowHelptext] = useState(false);
    const [showInput, setShowInput] = useState(false);

    const { ...forms } = genericFormAEM;

    const formKeys = Object.keys(forms);

    // Assume only 1 form
    const formKey = formKeys[0];
    const form = forms[formKey] as GenericRadioForm;

    const optionValues = form.options.map((option) => option.value);
    const hardValidation = validations?.hardValidations?.[formKey];
    const softValidation = validations?.softValidations?.[formKey];
    const [hardValidationMinValue, hardValidationMaxValue] = [
        hardValidation?.minValue ? Number(hardValidation.minValue) : 0,
        hardValidation?.maxValue ? Number(hardValidation.maxValue) : 20,
    ];
    const [softValidationMinValue, softValidationMaxValue] = [
        softValidation?.minValue ? Number(softValidation.minValue) : 0,
        softValidation?.maxValue ? Number(softValidation.maxValue) : 10,
    ];
    const inputMaxLength = hardValidation?.maxLength ? Number(hardValidation.maxLength) : 9;
    const errorRadioEmpty = hardValidation?.errorRadioEmpty ?? '';
    const errorHiddenInputMaxLength = hardValidation?.errorHiddenInputMaxLength ?? '';
    const errorHiddenInputNonDigit = hardValidation?.errorHiddenInputNonDigit ?? '';
    const errorHiddenInputEmpty = hardValidation?.errorHiddenInputEmpty ?? '';
    const infoHiddenInput = softValidation?.infoHiddenInput ?? '';
    
    const hiddenInput = hiddenInputs?.[formKey];
    const hideInputForTheseValues = hiddenInput?.values ?? [];

    const radioRef = useRef<HTMLDivElement>(null);
    const hiddenInputRef = useRef<HTMLInputElement>(null);

    // Decide to show or hide hidden input field
    const shouldShowHiddenInput = (value: string, array: string[]): boolean => {
        if (!value || Object.keys(array).length === 0) return false;
        
        return !hideInputForTheseValues.includes(value);
    };

    const isInRange = (
        value: string,
        minValue: number,
        maxValue: number,
    ): boolean => {
        if (value === '') return true;
        const parsedValue = parseInt(value, 10);

        return (minValue <= parsedValue && parsedValue <= maxValue);
    };

    useEffect(() => {
        // Prefill form
        const prefillValue = genericFormApiData?.[formKey]?.value;

        // Assume 1 form, maximum of 1 prefill value
        if (prefillValue) {
            const prefillString = prefillValue.toString();

            if (hiddenInput) {
                // Prefill radio
                if (!shouldShowHiddenInput(prefillString, hideInputForTheseValues)) {
                    setSelectedRadio(prefillString);
                }
                // Prefill hidden input
                else {
                    const inputValue = optionValues.find((v) => !hideInputForTheseValues.includes(v)) as string;

                    setSelectedRadio(inputValue);
                    setShowInput(true);
                    setHiddenInputValue(prefillString);
                    if (!isInRange(prefillString, softValidationMinValue, softValidationMaxValue)) {
                        setShowHelptext(true);
                    }
                }
            } else {
                setSelectedRadio(prefillString);
            }
        }
    }, []);

    useEffect(() => {
        // Invalid submission on 'confirm' button --> error message
        if (Object.hasOwn(errors, formKey)) {
            // Error on hidden input
            if (showInput) {
                hiddenInputRef.current?.focus();
                // Do not override existing error
                if (errorHiddenInput === '') setErrorHiddenInput(errorHiddenInputEmpty);
                setIsHiddenInputValid(false);
            }
            // Error on radio options
            else {
                radioRef.current?.focus();
                // Do not override existing error
                if (errorHiddenInput === '') setErrorRadio(errorRadioEmpty);
                setIsRadioValid(false);
            }
        }
    }, [errors]);

    const onRadioChange = (radioValue: string) => {
        setSelectedRadio(radioValue);
        setIsRadioValid(true);

        if (shouldShowHiddenInput(radioValue, hideInputForTheseValues)) {
            setShowInput(true);
            handleChange(formKey, hiddenInputValue);
            
            return;
        }
        setShowInput(false);
        handleChange(formKey, radioValue);
    };

    // Check for digits 0-9 only
    const isDigitsOnly = (value: string): boolean => {
        const regex = /^[0-9]*$/;
        
        return regex.test(value);
    };

    const onHiddenInputChange = (name: string, textInput: string) => {
        // Display invalid input to user
        setHiddenInputValue(textInput);

        // Filter out non-digits
        if (!isDigitsOnly(textInput)) {
            handleChange(formKey, null);
            setIsHiddenInputValid(false);
            setErrorHiddenInput(errorHiddenInputNonDigit);
            
            return;
        }

        // Hard Validation: Check input does not exceed max length
        if (textInput.length > inputMaxLength) {
            handleChange(formKey, null);
            setIsHiddenInputValid(false);
            setErrorHiddenInput(errorHiddenInputMaxLength);

            return;
        }

        // Hard Validation: Check digits are within range
        if (!isInRange(textInput, hardValidationMinValue, hardValidationMaxValue)) {
            handleChange(formKey, null);
            setIsHiddenInputValid(false);
            setErrorHiddenInput(errorHiddenInputMaxLength);

            return;
        }

        // Soft validation
        !isInRange(textInput, softValidationMinValue, softValidationMaxValue)
            ? setShowHelptext(true)
            : setShowHelptext(false);

        // Input is valid
        setIsHiddenInputValid(true);
        setErrorHiddenInput('');
        handleChange(formKey, textInput);
    };

    const formLabel = form.label as string;
    const textboxLabel = hiddenInput?.label;
    const formOptions = form.options;

    const helpTextWrapper = (text: string) =>
        showHelptextIcon ? (
            <div className="d-flex">
                <HelptextIconWrapper>
                    <Icon symbol="messageInfo16" />
                </HelptextIconWrapper>
                {text}
            </div>
        ) : (
            <>{text}</>
        );

    return (
        <>
            <div className="row m-0 p-0">
                <div className="p-0 col-12 mb-4" ref={radioRef} tabIndex={0}>
                    <RadioButtons
                        id={`${id}-radio`}
                        options={formOptions}
                        label={formLabel}
                        value={selectedRadio}
                        onChange={(_, value: string) => onRadioChange(value)}
                        isValid={isRadioValid}
                        errormessage={errorRadio}
                    />
                </div>

                {showInput && (
                    <div className="col-12 col-md-6 p-0 mb-1">
                        <TextField
                            errormessage={errorHiddenInput}
                            isValid={isHiddenInputValid}
                            autoComplete="off"
                            id={`${id}-hiddenInput`}
                            label={textboxLabel}
                            maxLength={inputMaxLength + 1} // Allow display of user input when input length exceeded by 1
                            name={`${id}-HiddenInput-name`}
                            type="text"
                            value={hiddenInputValue}
                            enableClearText
                            onChange={(name: string, value: string) => onHiddenInputChange(name, value)}
                            helptext={showHelptext && helpTextWrapper(infoHiddenInput)}
                            ref={hiddenInputRef}
                        />
                    </div>
                )}
            </div>
        </>
    );
};
